import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent,
  DialogTitle, FormControlLabel, IconButton, List, ListItem, ListItemText,
  MenuItem, Skeleton, Snackbar, Stack, Switch, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import { BsPencil, BsPlusLg, BsTrash, BsXLg } from 'react-icons/bs';

import axiosInstance from '../../axiosInstance';
import { useAuth } from '../../context/AuthContext';
import Fiche from '../Expert/Fiche';
import { euros, nombre } from '../Costings/constantes';
import { themeExpert } from '../../theme/expert';

const ELEMENTS = [
  ['PLAFOND', 'Plafond'], ['MUR', 'Mur'], ['SOL', 'Sol'],
  ['MENUISERIE', 'Menuiserie'], ['TOITURE', 'Toiture'], ['FACADE', 'Façade'],
  ['INSTALLATION', 'Installation technique'], ['MOBILIER', 'Mobilier'],
  ['AUTRE', 'Autre'],
];

const VIDE = {
  title: '', description: '', actif: true, share: false,
  element: '', mots_cles: '', pricingLists: [],
};

const libelleElement = (code) =>
  ELEMENTS.find(([c]) => c === code)?.[1] || null;

/** Les modèles de travaux : la séquence complète d'une réparation. */
function ModelPricingList() {
  const { user } = useAuth();
  // Publier un modèle l'envoie à toutes les organisations : c'est une
  // décision d'Iziclai. Le serveur le refuse aux autres ; l'écran ne le
  // propose pas.
  const equipe = Boolean(user?.is_staff);

  const [modeles, setModeles] = useState([]);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState('');
  const [message, setMessage] = useState('');
  const [occupe, setOccupe] = useState(false);

  const [fiche, setFiche] = useState(null);
  const [aSupprimer, setASupprimer] = useState(null);

  const [recherche, setRecherche] = useState('');
  const [propositions, setPropositions] = useState([]);

  const charger = useCallback(async () => {
    setChargement(true);
    try {
      const { data } = await axiosInstance.get('/model_pricings/');
      setModeles(Array.isArray(data) ? data : data.results);
      setErreur('');
    } catch {
      setErreur("Les modèles n'ont pas pu être chargés.");
    } finally {
      setChargement(false);
    }
  }, []);

  useEffect(() => { charger(); }, [charger]);

  // Le barème compte plusieurs milliers de lignes : on interroge le serveur
  // plutôt que de filtrer une page tronquée.
  useEffect(() => {
    if (recherche.trim().length < 2) { setPropositions([]); return undefined; }
    const minuterie = setTimeout(() => {
      axiosInstance.get('/pricinglist/',
                        { params: { query: recherche, page_size: 50 } })
        .then(({ data }) => setPropositions(data.results ?? data))
        .catch(() => setPropositions([]));
    }, 300);
    return () => clearTimeout(minuterie);
  }, [recherche]);

  const poser = (champ) => (e) => setFiche((etat) => ({
    ...etat,
    [champ]: e.target.type === 'checkbox' ? e.target.checked : e.target.value,
  }));

  const ajouterLigne = (ligne) => setFiche((etat) => (
    etat.pricingLists.some((l) => l.id === ligne.id)
      ? etat
      : { ...etat, pricingLists: [...etat.pricingLists, ligne] }));

  const retirerLigne = (id) => setFiche((etat) => ({
    ...etat, pricingLists: etat.pricingLists.filter((l) => l.id !== id),
  }));

  const deplacerLigne = (index, sens) => setFiche((etat) => {
    const cible = index + sens;
    if (cible < 0 || cible >= etat.pricingLists.length) return etat;
    const suite = [...etat.pricingLists];
    [suite[index], suite[cible]] = [suite[cible], suite[index]];
    return { ...etat, pricingLists: suite };
  });

  const ouvrir = (modele) => {
    setRecherche('');
    setPropositions([]);
    setFiche(modele
      ? { ...VIDE, ...modele, pricingLists: modele.pricingLists || [] }
      : { ...VIDE });
  };

  const enregistrer = async (e) => {
    e.preventDefault();
    setOccupe(true);
    try {
      const corps = {
        title: fiche.title.trim(), description: fiche.description,
        actif: fiche.actif,
        ...(equipe ? { share: fiche.share } : {}),
        element: fiche.element, mots_cles: fiche.mots_cles,
        // L'ordre compte : c'est celui dans lequel les lignes arriveront
        // dans le poste.
        pricingLists_ids: fiche.pricingLists.map((l) => l.id),
      };
      if (fiche.id) {
        await axiosInstance.put(`/model_pricings/${fiche.id}/`, corps);
      } else {
        await axiosInstance.post('/model_pricings/', corps);
      }
      setFiche(null);
      setMessage('Modèle enregistré');
      charger();
    } catch {
      setErreur("Le modèle n'a pas pu être enregistré.");
    } finally {
      setOccupe(false);
    }
  };

  const supprimer = async () => {
    setOccupe(true);
    try {
      await axiosInstance.delete(`/model_pricings/${aSupprimer.id}/`);
      setASupprimer(null);
      setMessage('Modèle supprimé');
      charger();
    } catch {
      setErreur("Le modèle n'a pas pu être supprimé.");
    } finally {
      setOccupe(false);
    }
  };

  const prix = (ligne) => {
    const min = nombre(ligne.unityMin);
    const max = nombre(ligne.unityMax);
    if (!min && !max) return '';
    return min && max && min !== max
      ? `${euros(min)} – ${euros(max)}` : euros(max || min);
  };

  return (
    <>
      <Fiche
        titre="Modèles de travaux"
        explication="La séquence complète d'une réparation, prête à être importée dans un poste de chiffrage."
        actions={(
          <Button size="small" variant="contained" startIcon={<BsPlusLg />}
                  onClick={() => ouvrir(null)}>
            Nouveau modèle
          </Button>
        )}
        sx={{ p: 0 }}
      >
        {erreur && <Alert severity="error" sx={{ m: 2 }}
                          onClose={() => setErreur('')}>{erreur}</Alert>}

        {chargement && (
          <Stack spacing={1} sx={{ p: 2 }}>
            {[...Array(3)].map((_, i) => <Skeleton key={i} height={36} />)}
          </Stack>
        )}

        {!chargement && modeles.length === 0 && (
          <Typography variant="body2" color="text.secondary"
                      sx={{ py: 4, textAlign: 'center' }}>
            Aucun modèle pour l'instant.
          </Typography>
        )}

        {!chargement && modeles.length > 0 && (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Modèle</TableCell>
                  <TableCell>Élément</TableCell>
                  <TableCell align="right">Lignes</TableCell>
                  <TableCell>Diffusion</TableCell>
                  <TableCell align="right">&nbsp;</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {modeles.map((modele) => (
                  <TableRow key={modele.id} hover
                            sx={{ opacity: modele.actif === false ? 0.5 : 1 }}>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {modele.title}
                      </Typography>
                      {modele.description && (
                        <Typography variant="caption" color="text.secondary">
                          {modele.description}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {libelleElement(modele.element) || (
                        <Tooltip title="Sans élément, ce modèle n'est jamais proposé face à un dommage.">
                          <Box component="span"
                               sx={{ color: 'warning.main' }}>aucun</Box>
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      {modele.pricingLists?.length ?? 0}
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} flexWrap="wrap"
                             useFlexGap>
                        {modele.actif === false
                          && <Chip size="small" label="inactif" />}
                        {modele.share && <Chip size="small" variant="outlined"
                                               label="Iziclai" />}
                      </Stack>
                    </TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      {/* Ces deux boutons n'avaient aucun gestionnaire :
                          ils ne faisaient rien. */}
                      <Tooltip title="Modifier">
                        <IconButton size="small"
                                    onClick={() => ouvrir(modele)}>
                          <BsPencil />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Supprimer">
                        <IconButton size="small"
                                    onClick={() => setASupprimer(modele)}>
                          <BsTrash />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Fiche>

      <ThemeProvider theme={themeExpert}>
        <Dialog open={fiche !== null} onClose={() => setFiche(null)}
                fullWidth maxWidth="md">
          <Box component="form" onSubmit={enregistrer} noValidate>
            <DialogTitle>
              {fiche?.id ? 'Modifier le modèle' : 'Nouveau modèle de travaux'}
            </DialogTitle>
            <DialogContent dividers>
              <Stack spacing={2.5}>
                <TextField label="Intitulé" value={fiche?.title || ''}
                           onChange={poser('title')} required autoFocus
                           helperText="Ex. : reprise d'un voile de verre au mur." />

                <TextField label="Description" multiline minRows={2}
                           value={fiche?.description || ''}
                           onChange={poser('description')}
                           helperText="Dans quel cas ce modèle s'emploie." />

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <TextField select label="Élément concerné"
                             value={fiche?.element || ''}
                             onChange={poser('element')}
                             sx={{ minWidth: 220 }}
                             helperText="Sans lui, le modèle n'est jamais proposé.">
                    <MenuItem value=""><em>Aucun</em></MenuItem>
                    {ELEMENTS.map(([code, libelle]) => (
                      <MenuItem key={code} value={code}>{libelle}</MenuItem>
                    ))}
                  </TextField>
                  <TextField label="Mots-clés" value={fiche?.mots_cles || ''}
                             onChange={poser('mots_cles')}
                             helperText="Termes attendus dans la composition du dommage : « voile de verre, toile de verre »." />
                </Stack>

                <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap>
                  <FormControlLabel
                    control={<Switch checked={Boolean(fiche?.actif)}
                                     onChange={poser('actif')} />}
                    label="Proposé aux dossiers" />
                  {equipe && (
                    <FormControlLabel
                      control={<Switch checked={Boolean(fiche?.share)}
                                       onChange={poser('share')} />}
                      label="Publier à toutes les organisations" />
                  )}
                </Stack>

                <Box>
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    Lignes du modèle
                    {fiche?.pricingLists?.length > 0
                      && ` (${fiche.pricingLists.length})`}
                  </Typography>
                  <Typography variant="caption" color="text.secondary"
                              component="div" sx={{ mb: 1.5 }}>
                    Dans l'ordre où elles arriveront au chiffrage : démolition
                    avant peinture.
                  </Typography>

                  <TextField
                    value={recherche}
                    onChange={(e) => setRecherche(e.target.value)}
                    placeholder="Chercher au barème (2 lettres minimum)…"
                    sx={{ mb: 1 }}
                  />

                  {propositions.length > 0 && (
                    <List dense sx={{ maxHeight: 200, overflowY: 'auto',
                                      border: '1px solid',
                                      borderColor: 'divider',
                                      borderRadius: 1, mb: 2 }}>
                      {propositions.map((ligne) => (
                        <ListItem key={ligne.id}
                                  secondaryAction={(
                                    <Button size="small"
                                            onClick={() => ajouterLigne(ligne)}>
                                      Ajouter
                                    </Button>
                                  )}>
                          <ListItemText primary={ligne.title}
                                        secondary={[prix(ligne), ligne.unit]
                                          .filter(Boolean).join(' · ')} />
                        </ListItem>
                      ))}
                    </List>
                  )}

                  {fiche?.pricingLists?.length > 0 ? (
                    <List dense sx={{ border: '1px solid',
                                      borderColor: 'divider',
                                      borderRadius: 1 }}>
                      {fiche.pricingLists.map((ligne, index) => (
                        <ListItem
                          key={ligne.id}
                          secondaryAction={(
                            <Stack direction="row">
                              <IconButton size="small" aria-label="Monter"
                                          disabled={index === 0}
                                          onClick={() => deplacerLigne(index, -1)}>
                                ↑
                              </IconButton>
                              <IconButton size="small" aria-label="Descendre"
                                          disabled={index === fiche.pricingLists.length - 1}
                                          onClick={() => deplacerLigne(index, 1)}>
                                ↓
                              </IconButton>
                              <IconButton size="small" aria-label="Retirer"
                                          onClick={() => retirerLigne(ligne.id)}>
                                <BsXLg size={12} />
                              </IconButton>
                            </Stack>
                          )}
                        >
                          <ListItemText
                            primary={`${index + 1}. ${ligne.title}`}
                            secondary={[prix(ligne), ligne.unit]
                              .filter(Boolean).join(' · ')} />
                        </ListItem>
                      ))}
                    </List>
                  ) : (
                    <Alert severity="info">
                      Aucune ligne. Cherchez au barème pour en ajouter.
                    </Alert>
                  )}
                </Box>
              </Stack>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}>
              <Button onClick={() => setFiche(null)} disabled={occupe}>
                Annuler
              </Button>
              <Button type="submit" variant="contained"
                      disabled={occupe || !fiche?.title?.trim()}>
                {occupe ? 'Enregistrement…' : 'Enregistrer'}
              </Button>
            </DialogActions>
          </Box>
        </Dialog>

        <Dialog open={aSupprimer !== null} onClose={() => setASupprimer(null)}>
          <DialogTitle>Supprimer ce modèle ?</DialogTitle>
          <DialogContent>
            <Typography variant="body2">
              « {aSupprimer?.title} » ne sera plus proposé ni importable. Les
              chiffrages déjà construits à partir de lui ne bougent pas.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setASupprimer(null)} disabled={occupe}>
              Annuler
            </Button>
            <Button onClick={supprimer} color="error" variant="contained"
                    disabled={occupe}>
              Supprimer
            </Button>
          </DialogActions>
        </Dialog>
      </ThemeProvider>

      <Snackbar open={Boolean(message)} autoHideDuration={3000}
                onClose={() => setMessage('')} message={message}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} />
    </>
  );
}

export default ModelPricingList;
